import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'
import {
  AskAiNewConversationValue,
  useAskAiQueryState,
} from '../../hooks/useAskAiQueryState'
import { useActiveAccount } from '../../utils/activeAccountContext'
import type { GlobalChatLayout } from './useGlobalChatLayout'
import { useGlobalChatLayout } from './useGlobalChatLayout'

interface AskAiState {
  /** Question typed in the command panel; sent automatically when the chat mounts. */
  initialQuestion: string | null
  inputFocusToken: number
  isOpen: boolean
  layout: GlobalChatLayout
  /** Conversation from the URL; `null` means a fresh chat. */
  selectedConversationId: string | null
}

interface AskAiActions {
  askQuestion: (question: string | null) => void
  close: () => void
  consumeInitialQuestion: () => void
  focusInput: () => void
  registerChatSender: (sender: ((text: string) => boolean) | null) => void
  selectConversation: (conversationId: string | null) => void
  sendMessage: (message: string) => void
  setLayout: (layout: GlobalChatLayout) => void
}

export type AskAiContextValue = CompositionContextValue<
  AskAiState,
  AskAiActions
>

export const AskAiContext =
  createNonNullableContext<AskAiContextValue>('AskAiContext')

export const useAskAi = AskAiContext.useRequiredContext

export interface AskAiProviderProps {
  readonly children: ReactNode
}

export function AskAiProvider({ children }: AskAiProviderProps) {
  const [initialQuestion, setInitialQuestion] = useState<string | null>(null)
  const [askAiPanel, setAskAiPanel] = useAskAiQueryState()
  const [layout, setGlobalChatLayout] = useGlobalChatLayout()
  const [sidebarAskAiPanel, setSidebarAskAiPanel] = useState<string | null>(
    null,
  )
  const [inputFocusToken, setInputFocusToken] = useState(0)
  const activeAccount = useActiveAccount()
  const chatSenderRef = useRef<((text: string) => boolean) | null>(null)

  const registerChatSender = (sender: ((text: string) => boolean) | null) => {
    chatSenderRef.current = sender
  }

  const openAskAiPanel = (panel: string) => {
    if (layout === 'sidebar') {
      setSidebarAskAiPanel(panel)
      void setAskAiPanel(panel)
      return
    }

    void setAskAiPanel(panel)
  }

  const focusInput = () => {
    setInputFocusToken((token) => {
      return token + 1
    })
  }

  const selectedAskAiPanel =
    layout === 'sidebar' ? (sidebarAskAiPanel ?? askAiPanel) : askAiPanel
  const isOpen = selectedAskAiPanel !== null && activeAccount !== null
  const selectedConversationId =
    selectedAskAiPanel === null ||
    selectedAskAiPanel === AskAiNewConversationValue
      ? null
      : selectedAskAiPanel

  const close = () => {
    chatSenderRef.current = null
    setInitialQuestion(null)
    setSidebarAskAiPanel(null)
    void setAskAiPanel(null)
  }

  const selectConversation = (conversationId: string | null) => {
    openAskAiPanel(conversationId ?? AskAiNewConversationValue)
  }

  const setLayout = (nextLayout: GlobalChatLayout) => {
    const nextPanel = selectedAskAiPanel ?? AskAiNewConversationValue

    if (nextLayout === 'sidebar') {
      setSidebarAskAiPanel(nextPanel)
      setGlobalChatLayout('sidebar')
      void setAskAiPanel(nextPanel)
      return
    }

    void setAskAiPanel(nextPanel)
    setGlobalChatLayout('dialog')
  }

  const askQuestion = (question: string | null) => {
    setInitialQuestion(question)
    openAskAiPanel(AskAiNewConversationValue)
    focusInput()
  }

  const sendMessage = (message: string) => {
    const trimmedMessage = message.trim()
    if (trimmedMessage.length === 0) {
      return
    }

    const targetPanel = selectedAskAiPanel ?? AskAiNewConversationValue
    openAskAiPanel(targetPanel)
    focusInput()

    if (chatSenderRef.current !== null) {
      const didSend = chatSenderRef.current(trimmedMessage)
      if (didSend) {
        return
      }
    }

    openAskAiPanel(AskAiNewConversationValue)
    setInitialQuestion(trimmedMessage)
  }

  return (
    <AskAiContext.Context
      value={{
        actions: {
          askQuestion,
          close,
          consumeInitialQuestion: () => {
            setInitialQuestion(null)
          },
          focusInput,
          registerChatSender,
          selectConversation,
          sendMessage,
          setLayout,
        },
        state: {
          initialQuestion,
          inputFocusToken,
          isOpen,
          layout,
          selectedConversationId,
        },
      }}
    >
      {children}
    </AskAiContext.Context>
  )
}
